import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, Kicker, Mail, Mark } from "./ui";
import { CONTACT_EMAIL } from "./constants";

/** The illustration that carries the whole idea: one person, two projects, two codes. */
const IsolationFigure: React.FC = () => {
  const { t } = useTranslation("homePage");

  return (
    <figure className="rounded-2xl border border-sand-200 bg-white p-6 sm:p-8">
      <div className="grid items-center gap-6 sm:grid-cols-[1fr,auto,1fr]">
        <div className="rounded-xl border border-sand-200 bg-sand-50 p-5">
          <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-400">
            {t("isolationFigure.sourceRecordLabel")}
          </p>
          <p className="mt-3 font-mono text-sm text-ink">120385-3299</p>
          <p className="font-mono text-sm text-ink-600">Anna Jónsdóttir</p>
          <p className="mt-3 text-xs text-ink-400">{t("isolationFigure.directIdentifiers")}</p>
        </div>

        {/* One arrow pointing down on narrow screens, two pointing right when the
            figure is laid out in columns. */}
        <div
          className="flex justify-center py-3 text-sand-300 sm:flex-col sm:gap-10 sm:py-0"
          aria-hidden="true"
        >
          <svg width="40" height="14" viewBox="0 0 40 14" fill="none" className="rotate-90 sm:rotate-0">
            <path d="M0 7h32" stroke="currentColor" strokeWidth="1.5" />
            <path d="M31 2l6 5-6 5" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
          <svg width="40" height="14" viewBox="0 0 40 14" fill="none" className="hidden sm:block">
            <path d="M0 7h32" stroke="currentColor" strokeWidth="1.5" />
            <path d="M31 2l6 5-6 5" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-accent/25 bg-accent-pale/50 p-5">
            <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-600">
              {t("isolationFigure.project1Label")}
            </p>
            <p className="mt-3 font-mono text-sm text-ink">
              {t("isolationFigure.subjectPrefix")} bK9xQ2
            </p>
          </div>
          <div className="rounded-xl border border-accent/25 bg-accent-pale/50 p-5">
            <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-600">
              {t("isolationFigure.project2Label")}
            </p>
            <p className="mt-3 font-mono text-sm text-ink">
              {t("isolationFigure.subjectPrefix")} 7pR4Lm
            </p>
          </div>
        </div>
      </div>
      <figcaption className="mt-6 border-t border-sand-200 pt-4 text-sm leading-relaxed text-ink-600">
        {t("isolationFigure.caption")}
      </figcaption>
    </figure>
  );
};

export const HomePage: React.FC = () => {
  const { t } = useTranslation("homePage");

  const STEPS = [
    { n: "01", title: t("steps.items.dataArrives.title"), body: t("steps.items.dataArrives.body") },
    {
      n: "02",
      title: t("steps.items.identifiersFound.title"),
      body: t("steps.items.identifiersFound.body"),
    },
    {
      n: "03",
      title: t("steps.items.personConfirms.title"),
      body: t("steps.items.personConfirms.body"),
    },
    {
      n: "04",
      title: t("steps.items.researchersWork.title"),
      body: t("steps.items.researchersWork.body"),
    },
  ];

  const ROLES = [
    { title: t("roles.items.researchers.title"), body: t("roles.items.researchers.body") },
    {
      title: t("roles.items.principalInvestigators.title"),
      body: t("roles.items.principalInvestigators.body"),
    },
    {
      title: t("roles.items.departmentHeads.title"),
      body: t("roles.items.departmentHeads.body"),
    },
    {
      title: t("roles.items.dataProviders.title"),
      body: t("roles.items.dataProviders.body"),
    },
  ];

  return (
    <>
      {/* Hero */}
      <section className="bg-ink text-white">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="max-w-3xl">
            <Kicker tone="light">{t("hero.kicker")}</Kicker>
            <h1 className="mt-5 font-display text-4xl leading-[1.08] sm:text-6xl">
              {t("hero.titleLine1")}
              <br />
              {t("hero.titleLine2")}
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-sand-100/80">
              {t("hero.body")}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/about"
                className="rounded-lg bg-accent-light px-5 py-3 text-sm font-semibold text-ink transition hover:bg-accent-pale"
              >
                {t("hero.links.howItWorks")}
              </Link>
              <Link
                to="/login"
                className="rounded-lg border border-white/25 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                {t("hero.links.login")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* The one rule */}
      <section className="border-b border-sand-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,26rem),minmax(0,1fr)]">
            <div>
              <Kicker>{t("theRule.kicker")}</Kicker>
              <h2 className="mt-4 font-display text-3xl leading-tight text-ink sm:text-[2.5rem]">
                {t("theRule.title")}
              </h2>
              <p className="mt-6 leading-relaxed text-ink-600">{t("theRule.body")}</p>
            </div>
            <IsolationFigure />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-sand-50">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
          <Kicker>{t("steps.kicker")}</Kicker>
          <h2 className="mt-4 font-display text-3xl text-ink sm:text-4xl">{t("steps.title")}</h2>
          <div className="mt-10 grid gap-x-10 gap-y-10 sm:grid-cols-2">
            {STEPS.map((step) => (
              <div key={step.n} className="flex gap-5">
                <span className="font-display text-2xl text-sand-300 leading-none">{step.n}</span>
                <div>
                  <h3 className="font-semibold text-ink">{step.title}</h3>
                  <p className="mt-2 leading-relaxed text-[#3B4A48]">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="border-y border-sand-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
          <div className="max-w-2xl">
            <Kicker>{t("roles.kicker")}</Kicker>
            <h2 className="mt-4 font-display text-3xl text-ink sm:text-4xl">{t("roles.title")}</h2>
            <p className="mt-5 leading-relaxed text-ink-600">{t("roles.body")}</p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ROLES.map((role) => (
              <Card key={role.title} title={role.title}>
                <p>{role.body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Honest limits */}
      <section className="bg-sand-100">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,22rem),minmax(0,1fr)]">
            <div>
              <Kicker>{t("limits.kicker")}</Kicker>
              <h2 className="mt-4 font-display text-3xl leading-tight text-ink">
                {t("limits.title")}
              </h2>
            </div>
            <div className="site-prose space-y-6 border-l border-sand-300 pl-8">
              <div>
                <p className="font-semibold text-ink">{t("limits.items.notAnonymization.title")}</p>
                <p>{t("limits.items.notAnonymization.body")}</p>
              </div>
              <div>
                <p className="font-semibold text-ink">
                  {t("limits.items.notEthicsReplacement.title")}
                </p>
                <p>{t("limits.items.notEthicsReplacement.body")}</p>
              </div>
              <div>
                <p className="font-semibold text-ink">{t("limits.items.notAccessDecision.title")}</p>
                <p>{t("limits.items.notAccessDecision.body")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to next */}
      <section className="bg-sand-50">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
          <div className="grid gap-6 lg:grid-cols-3">
            <Card
              title={t("nextSteps.cards.about.title")}
              footer={<Link to="/about">{t("nextSteps.cards.about.footerLink")}</Link>}
            >
              <p>{t("nextSteps.cards.about.body")}</p>
            </Card>
            <Card
              title={t("nextSteps.cards.security.title")}
              footer={<Link to="/security">{t("nextSteps.cards.security.footerLink")}</Link>}
            >
              <p>{t("nextSteps.cards.security.body")}</p>
            </Card>
            <Card
              title={t("nextSteps.cards.roadmap.title")}
              footer={<Link to="/roadmap">{t("nextSteps.cards.roadmap.footerLink")}</Link>}
            >
              <p>{t("nextSteps.cards.roadmap.body")}</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Access */}
      <section className="border-t border-sand-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8">
          <div className="flex flex-col items-start gap-8 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-5">
              <Mark className="h-11 w-11 shrink-0" />
              <div className="max-w-xl">
                <h2 className="font-display text-2xl text-ink">{t("access.title")}</h2>
                <p className="mt-2 leading-relaxed text-ink-600">{t("access.body")}</p>
                <p className="site-prose mt-3 text-[0.9375rem]">
                  {t("access.writeToUsBefore")}{" "}
                  <Mail address={CONTACT_EMAIL} subject={t("access.writeToUsSubject")} />.
                </p>
              </div>
            </div>
            <Link
              to="/login"
              className="shrink-0 rounded-lg bg-ink px-6 py-3 text-sm font-semibold text-white transition hover:bg-ink-800"
            >
              {t("access.login")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};
